import { createPatch } from 'rfc6902';
import * as z from 'zod';
import { Status } from '../util/schemas.js';
function getJsonPointerValue(target, path) {
    if (!path.startsWith('/'))
        return undefined;
    let current = target;
    for (const part of path.slice(1).split('/')) {
        if (current == null || typeof current !== 'object')
            return undefined;
        const key = part.replaceAll('~1', '/').replaceAll('~0', '~');
        current = current[key];
    }
    return current;
}
/**
 * `createPatch` with a `test` op before each change. The History drawer reads the old value from
 * the previous log's state, which does not exist on a record's first update; the test op keeps it.
 */
export function createPatchWithOldValues(prev, next) {
    return createPatch(prev, next).flatMap(op => {
        if (op.op !== 'add' && op.op !== 'replace')
            return [op];
        const oldValue = getJsonPointerValue(prev, op.path);
        if (oldValue === undefined)
            return [op];
        return [{ op: 'test', path: op.path, value: oldValue }, op];
    });
}
export const UpdateResourceStatusSchema = z.object({
    id: z.uuidv7(),
    status: Status,
});
export const DeleteResourceSchema = z.object({
    id: z.uuidv7(),
});
export const EventLogResources = {
    activeSubstance: 'active-substance',
    activeSubstanceCategoryRelation: 'active-substance-category-relation',
    consumable: 'consumable',
    tradeName: 'trade-name',
    tradeNameActiveSubstanceRelation: 'trade-name-active-substance-relation',
    tradeNameReconstitutionSolvent: 'trade-name-reconstitution-solvent',
    tradeNameConsumable: 'trade-name-consumable',
    substanceCategory: 'substance-category',
    diagnosis: 'diagnosis',
    administrationRoute: 'administration-route',
    applicationForm: 'application-form',
    containerType: 'container-type',
    containerMaterial: 'container-material',
    hazardClass: 'hazard-class',
    solutionType: 'solution-type',
    storageLabel: 'storage-label',
    medicineForm: 'medicine-form',
    drugCatalogItem: 'drug-catalog-item',
    protocol: 'protocol',
    medicationProfile: 'medication-profile',
    medicationProfileAllowedReconstitutionDiluent: 'medication-profile-allowed-reconstitution-diluent',
    medicationProfileAllowedCarrierSolution: 'medication-profile-allowed-carrier-solution',
    stabilityRule: 'stability-rule',
    medicationProfileStabilityRule: 'medication-profile-stability-rule',
    tradeNameStabilityRule: 'trade-name-stability-rule',
    stabilityRuleContainerMaterial: 'stability-rule-container-material',
    activeSubstanceDetails: 'active-substance-details',
    activeSubstanceHazardClass: 'active-substance-hazard-class',
    supportiveMedicationDetails: 'supportive-medication-details',
    solutionDetails: 'solution-details',
    drugsCategory: 'drugs-category',
    drugCatalogItemsCategory: 'drug-catalog-items-category',
    solutionDetailType: 'solution-detail-type',
    cancerType: 'cancer-type',
    company: 'company',
    treatmentChair: 'treatment-chair',
    prescribedName: 'prescribed-name',
    stockLocation: 'stock-location',
    insuranceCompany: 'insurance-company',
    generalPhysician: 'general-physician',
    protocolTherapySequence: 'protocol-therapy-sequence',
    protocolTherapySequenceMedicine: 'protocol-therapy-sequence-medicine',
    patientProtocol: 'patient-protocol',
    patientTherapySequence: 'patient-therapy-sequence',
    patientTherapySequenceMedicine: 'patient-therapy-sequence-medicine',
    patientTherapySequenceObservation: 'patient-therapy-sequence-observation',
    batch: 'batch',
    patientGfr: 'patient-gfr',
    patientCharacteristics: 'patient_characteristics',
    patientDoseRecalculation: 'patient-dose-recalculation',
    scaleAlibi: 'scale-alibi',
    restVial: 'rest-vial',
    return: 'return',
    discard: 'discard',
    order: 'order',
    compoundOrder: 'compound-order',
    compoundResult: 'compound-result',
    compoundMapping: 'compound-mapping',
    reportView: 'report-view',
};
/** True when an update's patches touch only the audit fields, i.e. nothing really changed. */
function isUpdateOnlyPatches(patches) {
    if (!patches || patches.length === 0)
        return false;
    // every op counts: a real change can be an `add`/`remove` (array append, new key), not just a `replace`
    return patches.every(({ path }) => path === '/updatedAt' || path === '/updatedBy');
}
export async function addEventLog(tx, payload, ctx) {
    if (payload.action?.toLowerCase() === 'update' && isUpdateOnlyPatches(payload.patches))
        return;
    const impersonatedBy = payload.impersonatedBy ?? ctx?.impersonatedBy ?? null;
    const search = buildEventLogSearch({ ...payload, impersonatedBy });
    await tx.mutate.eventLogs.insert({
        ...payload,
        impersonatedBy,
        search,
    });
}
export function buildEventLogSearch(payload) {
    const search = `${payload?.resource || ''} ${payload?.resourceId || ''} ${payload?.impersonatedBy || ''} ${getLogSearch(payload?.details)}`;
    return search;
}
// `items` is searched by its values, not its JSON; `variant` is a code nobody searches for.
const SEARCH_SKIPPED_KEYS = new Set(['items', 'variant']);
function getLogSearch(details) {
    const values = Object.entries(details ?? {})
        .filter(([key]) => !SEARCH_SKIPPED_KEYS.has(key))
        .map(([, value]) => value);
    const itemValues = parseItems(details?.items).flatMap(item => [item.label, item.value, item.old, item.new]);
    return [...values, ...itemValues]
        .filter(v => (typeof v === 'string' && v.length > 0) || typeof v === 'number')
        .join(' ');
}
function parseItems(raw) {
    if (typeof raw !== 'string')
        return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    }
    catch {
        return [];
    }
}
//# sourceMappingURL=util.js.map